import type { ComponentType, ReactNode } from 'react';
import {
  HomeIcon,
  AppsIcon,
  Inventory2Icon,
  AdminPanelSettingsIcon,
  MenuBookIcon,
  GridOnIcon,
  LightbulbIcon,
  AddBoxIcon,
  BugReportIcon,
  CalendarMonthIcon,
  ReportProblemIcon,
  EditNoteIcon,
  ViewKanbanIcon,
  LocalShippingIcon,
  SendIcon,
  BarcodeIcon,
  StorefrontIcon,
  ScienceIcon,
  TrendingUpIcon,
  GitHubIcon,
  HelpIcon,
  CloudUploadIcon,
  type IconProps,
  BatteryIcon,
  MoneyIcon,
  SettingsIcon,
  CheckCircleIcon,
  TableChartIcon,
  FormatColorTextIcon,
  InboxIcon,
  AssignmentIcon,
  ArticleIcon,
  SmartphoneIcon,
} from '@atlas/ui';
import i18n from '../i18n/index';
import type { DivisionId, UserRole } from '@atlas/shared';
import {
  canReachDivision,
  MARKETING_NAMING_PATH,
  MARKETING_PIPELINE_PATH,
  MARKETING_REQUESTS_PATH,
  PDLC_BOARD_PATH,
  SYNCFX_CONTENT_PATH,
} from '@atlas/shared';
import { adminTabFeatureId, isFeatureHidden, navFeatureId } from './featureIds';
import { ADMIN_TABS } from '../pages/admin/adminTabs.config';

/**
 * Re-exported from `@atlas/shared`, not redefined here. This module previously
 * declared its own `UserRole` union, which had already drifted — it was missing
 * `factory`, so a factory user silently failed every nav role gate.
 */
export type { UserRole };

export type NavSectionId =
  | 'products'
  | 'testing'
  | 'engineering'
  | 'paperwork'
  | 'commercial'
  | 'marketing'
  | 'admin'
  | 'support';

export interface NavItem {
  /** Path the link points to. Used as the React key. */
  to: string;
  /** Visible label for the rail / palette / breadcrumb. */
  label: string;
  /** Short description shown in the command palette / tooltip. */
  description?: string;
  /** Icon component class (passed as a component, not an instance). */
  icon: ComponentType<IconProps>;
  /** Optional pre-rendered icon (only used for backwards-compatibility with old AppBar). */
  iconNode?: ReactNode;
  /** Roles permitted to see this nav entry. Empty / undefined = visible to all authenticated users. */
  roles?: UserRole[];
  /**
   * Divisions permitted to see this nav entry (scope axis). Empty / undefined =
   * visible regardless of division. A user sees the item when they can reach at
   * least one listed division; `org` members reach everything.
   */
  divisions?: DivisionId[];
  /** When true, the link should be matched exactly (i.e. /products but not /products/123). */
  exact?: boolean;
  /** When true, the entry is rendered disabled with a "Coming soon" hint instead of acting as a link. */
  comingSoon?: boolean;
  /** When true, the entry opens in a new tab. */
  external?: boolean;
  /**
   * `'create'` moves the entry out of the sidebar list into its "New" menu. It
   * stays in NAV_SECTIONS so Feature Visibility, the command palette and
   * `navItemForPath` still see it.
   */
  placement?: 'create';
  /**
   * Pages that are tabs of one another. The sidebar shows the group as ONE
   * entry (the first member this user can see, under the group's label) and
   * each page renders `<NavTabs group=…>`. Every member keeps its own path and
   * nav entry, so its Feature Visibility id, saved sidebar orders and old links
   * all keep working.
   */
  tabGroup?: TabGroupId;
  /** Label on the group's tab strip when the sidebar label would repeat the group name. Plain English. */
  tabLabel?: string;
}

export type TabGroupId = 'tickets' | 'signoffs' | 'pricing' | 'evaluations';

/** Sidebar label for each tab group. Plain English, like the other recent nav labels. */
export const TAB_GROUP_LABELS: Record<TabGroupId, string> = {
  tickets: 'Tickets',
  signoffs: 'Sign-offs',
  pricing: 'Pricing',
  evaluations: 'Evaluations',
};

export interface NavSection {
  id: NavSectionId;
  label: string;
  items: NavItem[];
  /** Roles required to see any items in this section. Omitted = visible to all. */
  sectionRoles?: UserRole[];
  /** Divisions required to see this section (scope axis). Omitted = visible to all. */
  sectionDivisions?: DivisionId[];
}

const t = i18n.t.bind(i18n);

/**
 * Central nav config. Drives the side menu, the responsive AppBar,
 * and acts as a stable source of routes for the command palette.
 *
 * Role-gating is enforced here once; consumers just filter via `filterNavForUser`.
 *
 * NOTE: Labels and descriptions are resolved via i18n at call time, so NAV_SECTIONS
 * is a getter to ensure strings are always in the current language.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'products',
    // Hardcoded English section labels: the expanded drawer never shows them
    // (SHOW_SECTION_HEADERS is off); they only name the rail's groups, which
    // follow the PD default order in `personaSidebar.ts`.
    label: 'Products',
    items: [
      {
        to: '/',
        get label() { return t('nav.items.home.label'); },
        get description() { return t('nav.items.home.description'); },
        icon: HomeIcon,
        exact: true,
      },
      {
        to: '/products',
        get label() { return t('nav.items.products.label'); },
        get description() { return t('nav.items.products.description'); },
        icon: Inventory2Icon,
      },
      {
        to: '/products-spreadsheet',
        get label() { return t('nav.items.spreadsheet.label'); },
        get description() { return t('nav.items.spreadsheet.description'); },
        icon: GridOnIcon,
        // No `roles` on purpose: who reaches this page is set in Admin →
        // Feature Visibility, seeded to the old admin/pm list. A role list here
        // would overrule that switch — the config can only subtract.
      },
      {
        to: '/reports',
        // Hardcoded English, like Battery Info: no new locale key.
        label: 'Reports',
        description: 'Published spreadsheet views and their schedules',
        icon: ArticleIcon,
      },
      {
        to: '/battery-info',
        // Hardcoded English, unlike its neighbours: a nav key added to
        // en/translation.json obliges regenerating nine locales through the
        // Anthropic-backed generator, and `i18n/locales.test.ts` enforces exact
        // key parity, so a half-added key turns the frontend suite red.
        label: 'Battery Info',
        description: "Every fixture's battery specifications, in one list",
        icon: BatteryIcon,
        // No `roles`, same as the spreadsheet above — Admin → Feature
        // Visibility decides who reaches it.
      },
      {
        to: PDLC_BOARD_PATH,
        // Hardcoded English, like Battery Info: a new nav key obliges
        // regenerating nine locales through the Anthropic-backed generator.
        label: 'PDLC Board',
        description: "Every product's PDLC tasks on one page",
        icon: TableChartIcon,
        // No `roles`: Admin → Feature Visibility decides who reaches it, and
        // the API gates on the same id (PDLC_BOARD_FEATURE).
      },
      {
        to: '/barcodes',
        get label() { return t('nav.items.barcodes.label'); },
        get description() { return t('nav.items.barcodes.description'); },
        icon: BarcodeIcon,
      },
      {
        to: '/competitors',
        get label() { return t('nav.items.competitors.label'); },
        get description() { return t('nav.items.competitors.description'); },
        icon: StorefrontIcon,
      },
      {
        to: '/products/new',
        get label() { return t('nav.items.newProduct.label'); },
        get description() { return t('nav.items.newProduct.description'); },
        icon: AddBoxIcon,
        exact: true,
        placement: 'create',
      },
    ],
  },
  {
    id: 'testing',
    label: 'Testing',
    items: [
      {
        // Reuses the product tab's label key: the nine generated locales
        // already hold it, and a new key obliges a full regeneration.
        to: '/evaluations',
        tabGroup: 'evaluations',
        get label() { return t('products.detail.tabs.evaluations'); },
        icon: AssignmentIcon,
        // No `roles`: Admin → Feature Visibility decides who reaches it.
      },
      {
        to: '/lux-tests',
        tabGroup: 'evaluations',
        get label() { return t('nav.items.luxTests.label'); },
        get description() { return t('nav.items.luxTests.description'); },
        icon: TrendingUpIcon,
      },
      {
        to: '/asset-tracking',
        get label() { return t('nav.items.assetTracking.label'); },
        get description() { return t('nav.items.assetTracking.description'); },
        icon: ScienceIcon,
      },
      {
        to: '/fixture-comparison',
        get label() { return t('nav.items.fixtureComparison.label'); },
        get description() { return t('nav.items.fixtureComparison.description'); },
        icon: LightbulbIcon,
      },
      {
        to: '/apps',
        get label() { return t('nav.items.apps.label'); },
        get description() { return t('nav.items.apps.description'); },
        icon: AppsIcon,
      },
    ],
  },
  {
    id: 'engineering',
    label: 'Engineering',
    items: [
      {
        to: '/bugs',
        tabGroup: 'tickets',
        tabLabel: 'List',
        get label() { return t('nav.items.bugs.label'); },
        get description() { return t('nav.items.bugs.description'); },
        icon: BugReportIcon,
      },
      {
        to: '/bugs/board',
        tabGroup: 'tickets',
        get label() { return t('nav.items.taskBoard.label'); },
        get description() { return t('nav.items.taskBoard.description'); },
        icon: ViewKanbanIcon,
      },
      {
        to: '/bugs/calendar',
        tabGroup: 'tickets',
        get label() { return t('nav.items.bugCalendar.label'); },
        get description() { return t('nav.items.bugCalendar.description'); },
        icon: CalendarMonthIcon,
      },
      {
        to: '/dev-dashboard',
        get label() { return t('nav.items.devDashboard.label'); },
        get description() { return t('nav.items.devDashboard.description'); },
        icon: GitHubIcon,
        // No `roles`: Feature Visibility decides. The old DEV_DASHBOARD_ROLES
        // list was seeded into the settings document by migration 20260814-04,
        // and the server gates the same nav id, so the two cannot drift.
      },
      {
        to: '/docs',
        get label() { return t('nav.items.standardsDocs.label'); },
        get description() { return t('nav.items.standardsDocs.description'); },
        icon: MenuBookIcon,
      },
      {
        to: '/bugs/new',
        get label() { return t('nav.items.reportProductBug.label'); },
        get description() { return t('nav.items.reportProductBug.description'); },
        icon: ReportProblemIcon,
        placement: 'create',
      },
      {
        to: '/tech-writing/new',
        get label() { return t('nav.items.techWritingChange.label'); },
        get description() { return t('nav.items.techWritingChange.description'); },
        icon: EditNoteIcon,
        placement: 'create',
      },
    ],
  },
  {
    id: 'paperwork',
    label: 'Paperwork & samples',
    items: [
      {
        to: '/form-approvals',
        tabGroup: 'signoffs',
        get label() { return t('nav.items.formApprovals.label'); },
        get description() { return t('nav.items.formApprovals.description'); },
        icon: CheckCircleIcon,
        // Mirrors the route and the API: the roles that can send for approval.
        roles: ['admin', 'pm', 'engineer'],
      },
      {
        to: '/signature-requests',
        tabGroup: 'signoffs',
        get label() { return t('nav.items.signatureRequests.label'); },
        get description() { return t('nav.items.signatureRequests.description'); },
        icon: EditNoteIcon,
        // Mirrors the route and the API: the roles that can send for signature.
        roles: ['admin', 'pm', 'engineer'],
      },
      {
        to: '/form-requests',
        tabGroup: 'signoffs',
        get label() { return t('nav.items.formRequests.label'); },
        get description() { return t('nav.items.formRequests.description'); },
        icon: SendIcon,
        // Mirrors the route and the API: the roles that can send a request.
        roles: ['admin', 'pm', 'engineer'],
      },
      {
        to: '/tracking',
        get label() { return t('nav.items.tracking.label'); },
        get description() { return t('nav.items.tracking.description'); },
        icon: LocalShippingIcon,
      },
      {
        to: '/file-transfer',
        get label() { return t('nav.items.fileTransfer.label'); },
        get description() { return t('nav.items.fileTransfer.description'); },
        icon: CloudUploadIcon,
      },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    items: [
      {
        // Moved out of the admin panel 2026-08-26, same reasoning as bulletins:
        // pricing is PM work, and a door labelled "Admin" reads as off-limits
        // to the people meant to walk through it. Who reaches these two is set
        // in Admin → Feature Visibility (seeded to admin + pm), so no `roles`
        // here — a role list would overrule that switch.
        //
        // Labels reuse the `admin.tabs.*` keys the tabs already carried: the
        // nine generated locales already hold them, and a new key would oblige
        // a full regeneration to keep `i18n/locales.test.ts` green.
        to: '/price-lists',
        tabGroup: 'pricing',
        get label() { return t('admin.tabs.priceLists'); },
        icon: MoneyIcon,
      },
      {
        to: '/pricing-config',
        tabGroup: 'pricing',
        get label() { return t('admin.tabs.pricingConfig'); },
        icon: SettingsIcon,
      },
      {
        // The calculators were reachable only from the Apps launcher. Roles AND
        // divisions mirror their routes in App.tsx exactly; the launcher's own
        // name keys are reused so no locale needs regenerating.
        to: '/tools/pricing/pro',
        tabGroup: 'pricing',
        get label() { return t('apps.tools.proPricing.name'); },
        get description() { return t('apps.tools.proPricing.description'); },
        icon: MoneyIcon,
        roles: ['admin', 'pm'],
        divisions: ['chauvet-professional'],
      },
      {
        to: '/tools/pricing/dj',
        tabGroup: 'pricing',
        get label() { return t('apps.tools.djPricing.name'); },
        get description() { return t('apps.tools.djPricing.description'); },
        icon: MoneyIcon,
        roles: ['admin', 'pm'],
        divisions: ['chauvet-dj'],
      },
    ],
  },
  {
    id: 'marketing',
    // Hardcoded English, like the PDLC Board item: a new nav key obliges
    // regenerating nine locales through the Anthropic-backed generator.
    label: 'Marketing',
    items: [
      {
        to: MARKETING_PIPELINE_PATH,
        label: 'Pipeline',
        description: "Every product's marketing tasks, development to launch",
        icon: StorefrontIcon,
        // No `roles`: Admin → Feature Visibility decides who reaches it, and
        // the API gates on the same id (MARKETING_PIPELINE_FEATURE).
      },
      {
        to: MARKETING_NAMING_PATH,
        label: 'Naming',
        description: 'Proposed product names waiting on review',
        icon: FormatColorTextIcon,
        // No `roles`, same as Pipeline (MARKETING_NAMING_FEATURE).
      },
      {
        to: MARKETING_REQUESTS_PATH,
        label: 'Requests',
        description: 'Artwork, labels, CAD, photos and video asked of marketing',
        icon: InboxIcon,
        // No `roles`, same as Pipeline (MARKETING_REQUESTS_FEATURE).
      },
      {
        to: SYNCFX_CONTENT_PATH,
        label: 'SyncFX app',
        description: 'The Home cards and Help videos in the SyncFX app',
        icon: SmartphoneIcon,
        // No `roles`, same as Pipeline (SYNCFX_CONTENT_FEATURE). Publishing is admin-only in the API.
      },
      {
        // The read archive; /bulletins/manage and /bulletins/:id sit under it.
        // Reuses the archive page's own title key, same reason as Evaluations.
        to: '/bulletins',
        get label() { return t('bulletins.archive.title'); },
        icon: ArticleIcon,
        // No `roles`: Admin → Feature Visibility decides who reaches it.
      },
    ],
  },
  {
    id: 'admin',
    get label() { return t('nav.sections.admin'); },
    // Neither the section nor the item names roles any more: the entry is shown
    // to whoever has at least one admin tab to open (see `filterNavForUser`).
    // A role list here would hide the panel from someone an admin had just
    // granted a tab — and `nav:/admin` is NON_HIDEABLE, so it cannot be the
    // thing that hides it either.
    items: [
      {
        to: '/admin',
        get label() { return t('nav.items.adminPanel.label'); },
        get description() { return t('nav.items.adminPanel.description'); },
        icon: AdminPanelSettingsIcon,
      },
    ],
  },
  {
    id: 'support',
    get label() { return t('nav.sections.support'); },
    items: [
      {
        // "ATLAS Help" — in-app, ATLAS-authored guidance. Deliberately named to
        // contrast with the catalog's "Standards Docs" (/docs), which is the
        // external Chauvet product-standards library. Different content, owner,
        // and audience; only the labels could confuse, so they must stay distinct.
        to: '/help',
        get label() { return t('nav.items.help.label'); },
        get description() { return t('nav.items.help.description'); },
        icon: HelpIcon,
      },
      {
        to: '/report-bug',
        get label() { return t('nav.items.reportAtlasBug.label'); },
        get description() { return t('nav.items.reportAtlasBug.description'); },
        icon: BugReportIcon,
      },
    ],
  },
];

/**
 * Filter nav sections for a given user — hides items the user is not permitted
 * to access and drops sections that end up empty.
 */
export function filterNavForUser(
  sections: NavSection[],
  userRoles: readonly string[] | undefined,
  hidden?: ReadonlySet<string>,
  userDivisions?: readonly DivisionId[],
): NavSection[] {
  const roles = new Set(userRoles ?? []);
  const hasRole = (required?: UserRole[]) =>
    !required || required.length === 0 || required.some((r) => roles.has(r));
  // Scope axis: no required divisions = visible; otherwise the user must reach
  // one (org members reach all; an absent claim resolves to org → reaches all).
  const hasDivision = (required?: DivisionId[]) =>
    !required || required.length === 0 || required.some((d) => canReachDivision(userDivisions, d));

  // The Admin Panel entry is derived rather than role-listed: it is worth
  // showing exactly when the user has an admin tab they can open. Tabs that
  // name roles are hard-locked; the rest follow the same hidden set as
  // everything else here.
  const hasAnyAdminTab = ADMIN_TABS.some(
    (tab) =>
      (!tab.roles || tab.roles.some((r) => roles.has(r))) &&
      !(hidden && isFeatureHidden(adminTabFeatureId(tab.value), hidden)),
  );

  return sections
    .map((section) => {
      if (!hasRole(section.sectionRoles) || !hasDivision(section.sectionDivisions)) return null;
      const items = section.items.filter(
        (item) =>
          hasRole(item.roles) &&
          hasDivision(item.divisions) &&
          (item.to !== '/admin' || hasAnyAdminTab) &&
          !(hidden && isFeatureHidden(navFeatureId(item.to), hidden)),
      );
      if (items.length === 0) return null;
      return { ...section, items };
    })
    .filter((section): section is NavSection => section !== null);
}

/**
 * Flatten the nav into a single array of items the user can reach. Used by the
 * command palette to seed local results before hitting the search endpoint.
 */
export function flattenNavForUser(
  sections: NavSection[],
  userRoles: readonly string[] | undefined,
  hidden?: ReadonlySet<string>,
  userDivisions?: readonly DivisionId[],
): NavItem[] {
  return filterNavForUser(sections, userRoles, hidden, userDivisions).flatMap((section) => section.items);
}

/**
 * The nav item `pathname` belongs to, longest matching path first, or undefined
 * for a page with no nav entry (a detail route, an auth screen).
 *
 * This is what lets a hidden nav entry actually gate the page rather than only
 * removing the link: `ProtectedRoute` resolves the item for the current URL and
 * redirects when its feature id is hidden. Ignores role/division — those gates
 * live in ProtectedRoute already.
 */
export function navItemForPath(pathname: string): NavItem | undefined {
  return NAV_SECTIONS.flatMap((section) => section.items)
    .filter((item) =>
      item.exact || item.to === '/'
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`),
    )
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/**
 * Returns true if `pathname` should be treated as the "current" page for `item`.
 * `exact` items match the literal path; others match by prefix so child routes
 * keep their parent rail entry highlighted.
 */
export function isNavItemActive(
  item: NavItem,
  pathname: string,
  siblings?: NavItem[],
): boolean {
  if (item.exact) return pathname === item.to;
  if (item.to === '/') return pathname === '/';
  const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
  if (!matches) return false;
  if (siblings) {
    const moreSpecific = siblings.some(
      (other) =>
        other.to !== item.to &&
        other.to.startsWith(`${item.to}/`) &&
        (pathname === other.to || pathname.startsWith(`${other.to}/`)),
    );
    if (moreSpecific) return false;
  }
  return true;
}

/** Path of a tab group's home: its first member in config order. */
function tabGroupHome(group: TabGroupId): string {
  return NAV_SECTIONS.flatMap((s) => s.items).find((i) => i.tabGroup === group)!.to;
}

/**
 * Sidebar key for an entry: a tab group is keyed by its HOME path, so a saved
 * or default order that lists '/bugs' still places Tickets for someone who
 * cannot see /bugs itself and gets /bugs/board as the representative.
 */
export function sidebarKey(item: NavItem): string {
  return item.tabGroup ? tabGroupHome(item.tabGroup) : item.to;
}

/**
 * Collapse each tab group to ONE sidebar entry: the first member present in
 * `items` (already filtered for the user), relabelled with the group's label.
 * The other members are dropped; ungrouped items pass through untouched.
 */
export function collapseTabGroups(items: NavItem[]): NavItem[] {
  const seen = new Set<TabGroupId>();
  return items.flatMap((item) => {
    if (!item.tabGroup) return [item];
    if (seen.has(item.tabGroup)) return [];
    seen.add(item.tabGroup);
    return [{ ...item, label: TAB_GROUP_LABELS[item.tabGroup] }];
  });
}
